/**
 * Buzón de la asociación para las solicitudes que piden DIRIGIR un tipo de club.
 *
 * Por qué existe: aprobar una de estas no es admitir a un miembro, es NOMBRAR a
 * un director. Hasta ahora solo aparecía en el buzón del club afectado
 * (`/members/requests`), así que la asociación no se enteraba salvo que entrara
 * club por club — y si el club no tenía director general con cuenta, la
 * solicitud se quedaba pendiente sin que nadie pudiera verla.
 *
 * Quién aprueba: sigue pudiendo el director general del club (desde su buzón) o
 * un admin desde aquí; la autorización real la hace `approveJoinRequest`.
 */

import { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import {
  joinRequestService,
  type JoinRequest,
} from "@/lib/services/joinRequests";
import { clubService } from "@/lib/services/organization";
import JoinRequestCard, {
  useJoinRequestActions,
} from "@/pages/members/components/JoinRequestCard";
import { Inbox, Loader2, ShieldCheck } from "lucide-react";

export default function SectionDirectorRequestsPage() {
  const { profile } = useAuthStore();
  const { isUnionAdmin } = usePermissions();
  const associationId = profile?.associationId;

  const [allRequests, setAllRequests] = useState<JoinRequest[]>([]);
  // F12-13: nombramientos aprobados que NO se aplicaron (fallo silencioso).
  const [failed, setFailed] = useState<
    (JoinRequest & { sectionDirectorOutcome?: { reason?: string } })[]
  >([]);
  const [loading, setLoading] = useState(true);
  /** clubId → associationId, para recortar a la asociación del admin. */
  const [clubAssociations, setClubAssociations] = useState<
    Map<string, string | undefined>
  >(new Map());
  const [resolvingClubs, setResolvingClubs] = useState(false);
  const { processingId, error, notice, setError, approve, reject } =
    useJoinRequestActions();

  useEffect(() => {
    setLoading(true);
    const unsubscribe = joinRequestService.watchPendingSectionDirectorRequests(
      (data) => {
        setAllRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading section director requests:", err);
        setError("Error al cargar las solicitudes");
        setLoading(false);
      },
    );

    joinRequestService
      .getFailedDirectorAssignments()
      .then(setFailed)
      .catch((e) => console.warn("[SectionDirectorRequests] fallidos:", e));

    return () => unsubscribe();
  }, [setError]);

  // La solicitud no guarda associationId: se resuelve por su club (y se cachea,
  // porque varias solicitudes pueden ser del mismo club).
  useEffect(() => {
    if (isUnionAdmin) return; // el admin de unión las ve todas
    const missing = [
      ...new Set(allRequests.map((r) => r.clubId).filter(Boolean)),
    ].filter((id) => !clubAssociations.has(id));
    if (missing.length === 0) return;

    let active = true;
    setResolvingClubs(true);
    (async () => {
      const resolved = await Promise.all(
        missing.map(async (id) => {
          try {
            const club = await clubService.getById(id);
            return [id, club?.associationId] as const;
          } catch {
            return [id, undefined] as const;
          }
        }),
      );
      if (!active) return;
      setClubAssociations((prev) => {
        const next = new Map(prev);
        for (const [id, assoc] of resolved) next.set(id, assoc);
        return next;
      });
      setResolvingClubs(false);
    })();

    return () => {
      active = false;
    };
  }, [allRequests, clubAssociations, isUnionAdmin]);

  const requests = useMemo(() => {
    if (isUnionAdmin) return allRequests;
    if (!associationId) return [];
    // Mientras no sepamos de qué asociación es el club, no la mostramos: es
    // preferible que tarde un instante a enseñarle a un admin una solicitud de
    // otra asociación.
    return allRequests.filter(
      (r) => clubAssociations.get(r.clubId) === associationId,
    );
  }, [allRequests, associationId, clubAssociations, isUnionAdmin]);

  const busy = loading || resolvingClubs;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <span className="flex items-center gap-2">
            Solicitudes de dirección
            {!busy && requests.length > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">
                {requests.length}
              </span>
            )}
          </span>
        </h1>
        <p className="text-gray-500 mt-1 ml-[52px]">
          Personas que piden dirigir un tipo de club (Aventureros,
          Conquistadores o Guías Mayores)
          {isUnionAdmin ? "" : " en tu asociación"}. Al aprobarlas quedan como
          director/a de ese tipo de club.
        </p>
      </div>

      {/* F12-13: aprobadas cuyo nombramiento NO se aplicó. Antes esto no se
          veía en ninguna parte y la persona quedaba creyendo que dirigía. */}
      {failed.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="font-semibold text-amber-900 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            {failed.length} nombramiento(s) aprobado(s) que quedaron SIN aplicar
          </p>
          <p className="text-xs text-amber-800 mt-1">
            La membresía sí se creó, pero la persona no quedó como director/a.
            Resuelve la causa y asígnala desde el club → Directores de sección.
          </p>
          <ul className="mt-3 space-y-2">
            {failed.map((f) => (
              <li
                key={f.id}
                className="text-sm bg-white rounded-lg border border-amber-200 p-3"
              >
                <span className="font-medium text-gray-900">
                  {f.firstName} {f.lastName}
                </span>{" "}
                <span className="text-gray-600">
                  · {f.requestedSection} · {f.clubName}
                </span>
                <p className="text-xs text-amber-700 mt-1">
                  {f.sectionDirectorOutcome?.reason ?? "Motivo no registrado"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-red-500 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {notice && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-emerald-500 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            {notice}
          </p>
        </div>
      )}

      {busy ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Cargando solicitudes…
          </p>
        </div>
      ) : requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
          <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-700/50 mb-4">
            <Inbox className="w-10 h-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Todo al día
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Nadie está pidiendo dirigir un tipo de club ahora mismo. Las nuevas
            solicitudes aparecen aquí en tiempo real.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <JoinRequestCard
              key={request.id}
              request={request}
              processingId={processingId}
              onApprove={approve}
              onReject={reject}
              showClub
            />
          ))}
        </div>
      )}
    </div>
  );
}
